import {
  IconArrowOpenDownLine,
  IconBulletListLine,
  IconCodeLine,
  IconNumberedListLine,
  Menu,
} from "@instructure/ui";
import { $createCodeNode } from "@lexical/code";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { $createHeadingNode, $createQuoteNode } from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
} from "lexical";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const TextPlugin = ({ editor, blockType }) => {
  const { t } = useTranslation();

  const MenuDropdown = ({ editor, blockType }) => {
    const blockTypeToBlockName = {
      paragraph: t("rte.paragraph"),
      h1: t("rte.heading1"),
      h2: t("rte.heading2"),
      ul: t("rte.bulletedList"),
      ol: t("rte.numberedList"),
      quote: t("rte.quote"),
      code: t("rte.codeBlock"),
    };
    const [icon, setIcon] = useState(<span className="icon paragraph" />);

    const formatParagraph = () => {
      if (blockType !== "paragraph") {
        editor.update(() => {
          const selection = $getSelection();

          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createParagraphNode());
          }
        });
        setIcon(<span className="icon paragraph" />);
      }
    };

    const formatLargeHeading = () => {
      if (blockType !== "h1") {
        editor.update(() => {
          const selection = $getSelection();

          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createHeadingNode("h1"));
          }
        });
        setIcon(<span className="icon large-heading" />);
      }
    };

    const formatSmallHeading = () => {
      if (blockType !== "h2") {
        editor.update(() => {
          const selection = $getSelection();

          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createHeadingNode("h2"));
          }
        });
        setIcon(<span className="icon small-heading" />);
      }
    };

    const formatBulletList = () => {
      if (blockType !== "ul") {
        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND);
        setIcon(<IconBulletListLine />);
      } else {
        editor.dispatchCommand(REMOVE_LIST_COMMAND);
      }
    };

    const formatNumberedList = () => {
      if (blockType !== "ol") {
        editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND);
        setIcon(<IconNumberedListLine />);
      } else {
        editor.dispatchCommand(REMOVE_LIST_COMMAND);
      }
    };

    const formatQuote = () => {
      if (blockType !== "quote") {
        editor.update(() => {
          const selection = $getSelection();

          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createQuoteNode());
          }
        });
        setIcon(<span className="icon quote" />);
      }
    };

    const formatCode = () => {
      if (blockType !== "code") {
        editor.update(() => {
          const selection = $getSelection();

          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createCodeNode());
          }
        });
        setIcon(<IconCodeLine />);
      }
    };

    return (
      <Menu
        trigger={
          <button
            className="toolbar-item block-controls"
            aria-label={t("rte.formattingOptions")}
          >
            <span className="icon">{icon}</span>
            <span className="text">{blockTypeToBlockName[blockType]}</span>
            <IconArrowOpenDownLine size="x-small" />
          </button>
        }
      >
        <Menu.Item onClick={formatParagraph}>
          <div className="menu-item">
            <span className="icon paragraph" />
            {t("rte.paragraph")}
          </div>
        </Menu.Item>
        <Menu.Item onClick={formatLargeHeading}>
          <div className="menu-item">
            <span className="icon large-heading" />
            {t("rte.heading1")}
          </div>
        </Menu.Item>
        <Menu.Item onClick={formatSmallHeading}>
          <div className="menu-item">
            <span className="icon small-heading" />
            {t("rte.heading2")}
          </div>
        </Menu.Item>
        <Menu.Item onClick={formatBulletList}>
          <div className="menu-item">
            <IconBulletListLine />
            {t("rte.bulletedList")}
          </div>
        </Menu.Item>
        <Menu.Item onClick={formatNumberedList}>
          <div className="menu-item">
            <IconNumberedListLine />
            {t("rte.numberedList")}
          </div>
        </Menu.Item>
        <Menu.Item onClick={formatQuote}>
          <div className="menu-item">
            <span className="icon quote" />
            {t("rte.quote")}
          </div>
        </Menu.Item>
        <Menu.Item onClick={formatCode}>
          <div className="menu-item">
            <IconCodeLine /> {t("rte.codeBlock")}
          </div>
        </Menu.Item>
      </Menu>
    );
  };

  return <MenuDropdown editor={editor} blockType={blockType} />;
};
